import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Section, SectionHeading, Eyebrow } from "@/components/Bits";
import { Reveal } from "@/components/Reveal";
import { useDialogs } from "@/components/GlobalDialogs";

export default function About() {
  const { openJoin, openCollaborate } = useDialogs();
  return (
    <div>
      <section className="relative overflow-hidden border-b border-border noise">
        <div className="ytca-container py-16 sm:py-24">
          <Eyebrow>About YTCA</Eyebrow>
          <h1 className="mt-5 max-w-4xl font-display text-4xl font-semibold leading-[1.02] tracking-tight sm:text-6xl">
            A movement to make creativity part of everyday life.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
            You Too Can Art isn't an art school or a workshop business. It's a community built on a
            single, stubborn belief: that everyone is creative, and art belongs in the middle of ordinary life.
          </p>
        </div>
      </section>

      {/* The Movement */}
      <Section>
        <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
          <SectionHeading eyebrow="The Movement" title="Why YTCA exists" />
          <div className="space-y-5 text-lg leading-relaxed text-foreground/80">
            <p>Somewhere along the way, most of us were told that creativity is a talent handed to a lucky few. That you either have it or you don't.</p>
            <p>We think that's the biggest lie in creative life. Children draw before they can write. We hum before we can read music. The urge to make is universal — what's rare is a world that keeps making room for it.</p>
            <p>YTCA is our attempt to make that room. Through experiences, learning, opportunities and a physical space, we want to bring creativity back into everyday life — not as a career for a few, but as a practice for everyone.</p>
          </div>
        </div>
      </Section>

      {/* Our Mission */}
      <section className="ytca-section bg-[#141414] text-white noise">
        <div className="ytca-container">
          <Reveal>
            <Eyebrow className="text-white/70">Our Mission</Eyebrow>
            <p className="mt-6 max-w-4xl font-display text-3xl font-medium leading-snug sm:text-5xl">
              To create a new era in which India becomes an artistic capital of the world — and art
              becomes an appreciated and respectable profession.
            </p>
            <div className="mt-10 grid gap-5 md:grid-cols-3">
              {[
                { t: "Make", d: "Put making back into everyday life for as many people as possible." },
                { t: "Belong", d: "Grow a genuine community where creatives support each other." },
                { t: "Sustain", d: "Build real pathways for artists to earn from their craft." },
              ].map((m) => (
                <div key={m.t} className="rounded-2xl border border-white/10 bg-white/5 p-6">
                  <h3 className="font-display text-xl font-semibold">{m.t}</h3>
                  <p className="mt-2 text-sm text-white/70">{m.d}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Founder */}
      <Section>
        <SectionHeading eyebrow="Founder" title="The story behind YTCA" />
        <div className="mt-10 grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <Reveal>
            <div className="overflow-hidden rounded-3xl border border-border">
              <img src="https://images.unsplash.com/photo-1531891437562-4301cf35b7e4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000" alt="YTCA founder portrait placeholder" className="aspect-[4/5] w-full object-cover" />
            </div>
          </Reveal>
          <div className="space-y-5 text-lg leading-relaxed text-foreground/80">
            <p>YTCA began with a simple frustration: so many people say "I wish I could draw" or "I used to paint" — as if creativity were a door that had quietly closed on them.</p>
            <p>It hadn't. What most people needed wasn't talent, but permission, a method, and company. YTCA was founded to offer all three.</p>
            <p className="text-sm text-muted-foreground">(This is placeholder text. The full founder story will be added here.)</p>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button onClick={openJoin} className="rounded-full" data-testid="about-join-button">Join YTCA</Button>
              <Button onClick={openCollaborate} variant="outline" className="rounded-full" data-testid="about-collaborate-button">Collaborate With Us</Button>
            </div>
          </div>
        </div>
      </Section>

      <section className="border-t border-border bg-secondary/40">
        <div className="ytca-container flex flex-col items-center gap-4 py-14 text-center">
          <h2 className="font-display text-2xl font-semibold sm:text-3xl">See what the movement is making.</h2>
          <div className="flex flex-wrap justify-center gap-3">
            <Button asChild className="rounded-full" data-testid="about-events-button"><Link to="/events">Explore Events</Link></Button>
            <Button asChild variant="outline" className="rounded-full" data-testid="about-learn-button"><Link to="/learn-earn">Learn & Earn</Link></Button>
          </div>
        </div>
      </section>
    </div>
  );
}
